import { Grid, List, ListItem, ListItemText, ListSubheader, Paper, Typography } from '@material-ui/core';
import { unstable_Box as Box } from '@material-ui/core/Box';
import { withStyles } from '@material-ui/core/styles';
import AccountIcon from '@material-ui/icons/AccountCircle';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircle';
import classNames from 'classnames';
import gql from 'graphql-tag';
import moment from 'moment';
import * as qs from 'query-string';
import React from 'react';

import {
  Account,
  App,
  CreditCard,
  Membership,
  Payline,
  PaymentProvider,
  helpers,
} from '@aps-management/primapp-common';

import Payment from '_components/Payment';
import { Screen } from '_components/core';
import { DialogPaymentIP } from '_components/elements';
import DialogWallet3DS from '_components/elements/DialogWallet3DS';
import apolloClient from '_utils/apolloClient';

/* */
import i18n from '_utils/i18n';
import masterClient from '_utils/masterClient';

// Règlement lu directement dans le master (source stable), utilisé en repli
// quand le WS live du club ne renvoie plus la cotisation (saison clôturée).
const CHECK_MEMBERSHIP_SCHEDULE_FULL = gql`
  query CheckMembershipSchedule($golfId: String!, $accountId: String!, $reference: String!) {
    checkMembershipSchedule(golfId: $golfId, accountId: $accountId, reference: $reference) {
      reference
      year
      amount
      frequency
      createdAt
      total {
        amount
        paid
        due
      }
      paymentSchedule {
        date
        amount
        status
      }
    }
  }
`;

/* */
const styles = (theme) => ({
  listTitle: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    marginBottom: `${theme.spacing.unit * 2}px`,
  },
  listContainer: {
    marginBottom: `${theme.spacing.unit * 5}px`,
  },
  totalLine: {
    background: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
  detailAmount: {
    minWidth: 80,
    textAlign: 'right',
  },
  remaining: { color: theme.palette.error.main },
  fullyPaid: { color: '#4caf50' },
  bold: { fontWeight: 'bold' },
});

/* */
class FullyPayMembership extends React.Component {
  /* */
  constructor(props) {
    super(props);

    this.state = {
      // ux
      error: null,
      screenLoading: true,
      buttonLoading: false,
      waiting: false,
      status: null,
      paylineToken: null,
      // data
      golf: null,
      account: null,
      membership: null,
      creditCard: null, // credit card existante
      isFullyCompleted: false, // membershipSchedule existant - paiement effectué
      // Wallet 3DS flow
      wallet3DS: {
        open: false,
        threeDSData: null,
        subscriber: null,
        amount: null,
        reference: null,
      },
    };

    this.paylineWidgetData = null;
    this.isPaylinePaymentHandled = false;
    this.removePaylineListeners = null;
  }

  static loadPaylineScript = () =>
    new Promise((resolve, reject) => {
      if (window.Payline && window.Payline.Api) {
        resolve();
        return;
      }

      const existingScript = document.getElementById('payline-widget-script');
      if (existingScript) {
        existingScript.addEventListener('load', resolve);
        existingScript.addEventListener('error', reject);
        return;
      }

      const script = document.createElement('script');
      script.id = 'payline-widget-script';
      script.src = 'https://payment.cdn.payline.com/cdn/scripts/widget-min.js';
      script.async = true;
      script.addEventListener('load', resolve);
      script.addEventListener('error', reject);
      document.body.appendChild(script);
    });

  showPaylineWidget = (token) => {
    this.removePaylineWidget();

    const widgetContainer = document.getElementById('PaylineWidget');
    if (!widgetContainer) {
      throw new Error('Impossible d’afficher le widget de paiement.');
    }

    widgetContainer.setAttribute('data-token', token);
    widgetContainer.setAttribute('data-template', 'column');
    widgetContainer.setAttribute('data-embeddedredirectionallowed', 'false');
    widgetContainer.setAttribute('data-auto-init', 'false');

    this.setupPaylineListeners();

    try {
      if (window.Payline && window.Payline.Api && window.Payline.Api.reset) {
        window.Payline.Api.reset(token, 'column');
      } else {
        if (window.Payline && window.Payline.Api && window.Payline.Api.init) {
          window.Payline.Api.init();
        }
        window.Payline.Api.show();
      }
    } catch (e) {
      throw e;
    }
  };

  setupPaylineListeners = () => {
    const api = window.Payline && window.Payline.Api;
    const listeners = [];

    const bindApiEvent = (eventName, handler) => {
      if (!api || !api.on || !eventName) return;

      api.on(eventName, handler);

      listeners.push(() => {
        if (api && api.off) {
          api.off(eventName, handler);
        }
      });
    };

    const bindWindowEvent = (eventName, handler) => {
      window.addEventListener(eventName, handler);
      listeners.push(() => {
        window.removeEventListener(eventName, handler);
      });
    };

    const onPaymentSuccess = (eventData) => {
      this.handlePaylinePaymentSuccess(eventData);
    };

    const onPaymentFailure = (eventData) => {
      this.handlePaylinePaymentError(eventData, 'Le paiement a échoué. Veuillez réessayer.');
    };

    const onPaymentCancel = (eventData) => {
      this.handlePaylinePaymentError(eventData, 'Le paiement a été annulé.');
    };

    bindApiEvent(api && api.EVENT && api.EVENT.PAYMENT_SUCCESS, onPaymentSuccess);
    bindApiEvent(api && api.EVENT && api.EVENT.PAYMENT_FAILURE, onPaymentFailure);
    bindApiEvent(api && api.EVENT && api.EVENT.PAYMENT_CANCELED, onPaymentCancel);
    bindApiEvent(api && api.EVENT && api.EVENT.PAYMENT_CANCEL, onPaymentCancel);
    bindWindowEvent('payline-payment-success', onPaymentSuccess);
    bindWindowEvent('payline-payment-failure', onPaymentFailure);
    bindWindowEvent('payline-payment-cancel', onPaymentCancel);

    this.removePaylineListeners = () => {
      listeners.forEach((removeListener) => removeListener());
      this.removePaylineListeners = null;
    };
  };

  removePaylineWidget = () => {
    const widgetContainer = document.getElementById('PaylineWidget');

    if (widgetContainer && widgetContainer.children.length > 0) {
      try {
        if (window.Payline && window.Payline.Api && window.Payline.Api.hide) {
          const hideRes = window.Payline.Api.hide();
          if (hideRes && hideRes.catch) {
            hideRes.catch(() => null);
          }
        }
      } catch (e) {}
    }

    if (widgetContainer) {
      widgetContainer.removeAttribute('data-token');
      widgetContainer.innerHTML = '';
    }

    if (this.removePaylineListeners) {
      this.removePaylineListeners();
    }
  };

  handlePaylinePaymentSuccess = (eventData = {}) => {
    if (this.isPaylinePaymentHandled || !this.paylineWidgetData || !this.paylineWidgetData.token) {
      return;
    }

    this.isPaylinePaymentHandled = true;

    const { membership } = this.state;
    const token = (eventData && eventData.token) || this.paylineWidgetData.token;
    const { amount } = this.transaction;

    this.removePaylineWidget();
    this.setState({
      error: null,
      waiting: true,
      status: 'Vérification du paiement...',
      buttonLoading: true,
    });

    return Payline.api
      .checkPayment(apolloClient, { token })
      .then((result) => {
        if (!result) {
          throw new Error('Impossible de vérifier le paiement Payline.');
        }

        this.setState({ status: 'Nous confirmons votre paiement au golf...' });

        return this.setMembershipPayment({
          amount,
          callNo: result.authorizationNumber || result.transactionId || token || '',
          transactionNo: result.transactionId,
          card: result.maskedCardNumber || '****',
          brand: result.cardBrand,
          country: result.cardCountry,
        });
      })
      .then(() => {
        membership.total.due -= amount;
        membership.total.paid += amount;
        this.setState({
          status: null,
          waiting: false,
          buttonLoading: false,
          isPaid: true,
          isFullyCompleted: true,
        });
      })
      .catch((err) => {
        this.isPaylinePaymentHandled = false;
        this.setState({
          status: null,
          waiting: false,
          buttonLoading: false,
          paylineToken: null,
          error: err.message,
        });
        this.removePaylineWidget();
      });
  };

  handlePaylinePaymentError = (eventData = {}, defaultMessage = 'Le paiement a échoué. Veuillez réessayer.') => {
    this.removePaylineWidget();

    this.setState({
      waiting: false,
      buttonLoading: false,
      status: null,
      paylineToken: null,
      error: (eventData && eventData.message) || defaultMessage,
    });

    this.isPaylinePaymentHandled = false;
  };

  /* */
  componentDidMount() {
    this.load();
  }

  componentWillUnmount() {
    this.removePaylineWidget();
  }

  /* */
  load = () => {
    const { reference } = this.props.match.params;
    const { token } = qs.parse(this.props.location.search);

    if (token && reference) {
      Account.api
        .getAccountByToken(apolloClient, { token })
        .then((account) => {
          if (account) {
            const matching = account.matches.find((m) => (m.token === token ? m : false));
            if (!matching) {
              throw new Error('Client non trouvé');
            }

            return App.api.getGolfInfo(apolloClient, { id: matching.golfId }).then(({ golf }) => {
              document.title = (golf && golf.name) || 'prima.golf';

              // set locale
              i18n.locale = golf.locale;
              this.currency = golf.currency;

              // build session 3DS
              this.session3DS = {
                address: {
                  city: golf.city,
                  countryCode: 250, // golf.country
                  line1: golf.name,
                  zipCode: golf.postalCode,
                },
                customer: {
                  email: account.email,
                  lastName: account.lastname,
                  firstName: account.firstname,
                  phone: account.phone,
                },
              };

              return Promise.all([
                // Le WS du club peut être indisponible ou en erreur : ne pas
                // faire échouer toute la page, le repli master peut suffire.
                Membership.api
                  .getExercises(apolloClient, {
                    golfId: golf.id,
                    accountId: account.id,
                  })
                  .catch(() => null),
                masterClient
                  .query({
                    query: CHECK_MEMBERSHIP_SCHEDULE_FULL,
                    variables: {
                      reference,
                      golfId: golf.id,
                      accountId: account.id,
                    },
                    fetchPolicy: 'no-cache',
                  })
                  .then((res) => res.data.checkMembershipSchedule),
              ]).then((data) => {
                const scheduleFromMaster = data[1];
                const wsAvailable = !!(data[0] && data[0].exercises && data[0].exercises.exercises);

                let membership = null;

                // 1) Source primaire : le WS live du club (exercice courant).
                if (wsAvailable) {
                  data[0].exercises.exercises.forEach((e) => {
                    (e.memberships || []).forEach((m) => {
                      // Paiement comptant uniquement
                      if (m.reference === reference && (m.paymentSchedule || []).length === 0) {
                        membership = m;
                        // Force le calcul du montant restant
                        membership.total.due = m.total.amount - m.total.paid;

                        // Init transaction à effectuer
                        this.transaction = {
                          amount: m.total.amount - m.total.paid,
                          reference: `MEM-${m.reference}`,
                        };
                      }
                    });
                  });
                }

                let isFullyCompleted = false;
                if (membership && scheduleFromMaster && scheduleFromMaster.reference && scheduleFromMaster.frequency === 'single') {
                  // Un règlement existe déjà côté master : le WS du club peut
                  // simplement ne pas encore refléter le paiement web.
                  isFullyCompleted = true;
                }

                // 2) Repli : le WS ne renvoie plus cette cotisation (saison
                //    clôturée ou WS muet), mais le master connaît le règlement.
                if (!membership && scheduleFromMaster && scheduleFromMaster.reference) {
                  if (scheduleFromMaster.frequency !== 'single') {
                    // Lien comptant reçu pour une cotisation en prélèvements :
                    // la page échéancier sait l'afficher depuis le master.
                    this.props.history.replace(`/membership/confirm/${reference}${this.props.location.search}`);
                    return null;
                  }

                  if (scheduleFromMaster.total.due <= 0) {
                    membership = {
                      reference: scheduleFromMaster.reference,
                      year: scheduleFromMaster.year,
                      createdAt: scheduleFromMaster.createdAt,
                      period: null,
                      members: [
                        {
                          category: `Cotisation ${scheduleFromMaster.year}`,
                          firstName: account.firstname || '',
                          lastName: account.lastname || '',
                          lines: [
                            {
                              name: `Cotisation ${scheduleFromMaster.year}`,
                              amount: scheduleFromMaster.total.amount,
                            },
                          ],
                        },
                      ],
                      total: { ...scheduleFromMaster.total },
                      paymentSchedule: [],
                    };
                    this.transaction = {
                      amount: 0,
                      reference: `MEM-${scheduleFromMaster.reference}`,
                    };
                    isFullyCompleted = true;
                  } else {
                    // Restant dû connu du seul master : ne JAMAIS encaisser sur
                    // cette base. findByReference renvoie la ligne la plus
                    // ancienne de (golf_id, reference), et chaque paiement
                    // comptant crée une nouvelle ligne : au re-clic du lien, la
                    // ligne retournée serait celle sans transaction et le
                    // client repaierait une cotisation déjà réglée.
                    throw new Error("Le règlement en ligne de cette cotisation n'est plus disponible. Veuillez contacter votre golf.");
                  }
                }

                if (!membership) {
                  throw new Error(wsAvailable ? 'Cotisation non trouvée' : 'Impossible de se connecter au golf. Veuillez réessayer ultérieurement.');
                }

                return CreditCard.api
                  .getCreditCards(apolloClient, { accountId: account.id })
                  .then(({ creditCards }) => {
                    // Garde-fou : seule une carte disposant d'un wallet Payline réel (walletId)
                    // peut être débitée en 1 clic. Une carte sans walletId (enregistrée avant le
                    // correctif ou via un chemin legacy) produirait subscriber.id=null : mutation
                    // rejetée. On ne la propose donc pas : l'utilisateur ressaisit sa carte (le
                    // widget crée un vrai wallet), ce qui répare la situation de lui-même.
                    let myCard = null;
                    const usable = (creditCards || []).find((c) => c && c.walletId);
                    if (usable) {
                      myCard = {
                        ...usable,
                        ...CreditCard.functions.getStatus(usable.dueDate),
                      };
                    }

                    this.setState({
                      golf,
                      account,
                      membership,
                      isFullyCompleted,
                      creditCard: myCard,
                    });
                  });
              });
            });
          }
          throw new Error('Client non trouvé');
        })
        .catch((error) => this.setState({ error }))
        .finally(() => this.setState({ screenLoading: false }));
    }
  };

  /* */
  renderHeader() {
    const { golf, account, isFullyCompleted } = this.state;
    const { amount: dueAmount } = this.transaction;
    // const title = membership.members[0].category;

    return (
      <div>
        <Typography variant='h5' gutterBottom>
          {golf.name}
        </Typography>
        <Typography variant='subtitle1' gutterBottom>
          {'Paiement par carte bancaire'}
        </Typography>
        <br />
        <Typography variant='body2'>
          {'Bonjour '}
          <b>{`${account.firstname} ${account.lastname}`}</b>
          {','}
        </Typography>
        {!isFullyCompleted && (
          <React.Fragment>
            {dueAmount > 0 ? (
              <Typography variant='body2'>
                {'Le réglement de votre cotisation de '}
                <b>{`${i18n.l('currency', dueAmount / 100)}`}</b>
                {' se fera comptant sur votre carte bancaire.'}
              </Typography>
            ) : (
              <Typography variant='body2'>
                {'Le réglement de votre cotisation se fera comptant sur votre carte bancaire.'}
              </Typography>
            )}
            <Typography variant='body2'>
              {'Pour ce faire, veuillez renseigner / valider vos coordonnées de cartes bancaires ci-dessous.'}
            </Typography>
          </React.Fragment>
        )}
        <br />
      </div>
    );
  }

  /* */
  renderInfosGrid() {
    const { classes } = this.props;
    const { membership } = this.state;

    return (
      <Grid item md={6}>
        <Typography variant='h6'>{'Informations'}</Typography>
        <br />
        {membership.period && (
          <Typography>
            {`Période du ${i18n.l('date.formats.default', moment(membership.period.from).toDate())} `}
            {`au ${i18n.l('date.formats.default', moment(membership.period.to).toDate())}`}
          </Typography>
        )}
        {membership.createdAt && (
          <Typography>{`Établi le ${i18n.l('date.formats.default', moment(membership.createdAt).toDate())}`}</Typography>
        )}
        <br />
        {membership.members.map((member, i) => this.renderMemberDetails(member, i))}
        <Paper className={classes.listContainer}>
          <List
            disablePadding
            subheader={
              <ListSubheader color='inherit' className={classes.totalLine}>
                {'Total'}
              </ListSubheader>
            }
          >
            <ListItem>
              <ListItemText primary='Montant' primaryTypographyProps={{ color: 'primary', variant: 'body2' }} />
              <Typography variant='body2'>{i18n.l('currency', membership.total.amount / 100)}</Typography>
            </ListItem>
            <ListItem>
              <ListItemText primary='Déjà réglé' primaryTypographyProps={{ color: 'primary', variant: 'body2' }} />
              <Typography variant='body2'>{i18n.l('currency', membership.total.paid / 100)}</Typography>
            </ListItem>
            <ListItem>
              <ListItemText primary='Restant dû' primaryTypographyProps={{ color: 'primary', variant: 'body2' }} />
              <Typography
                variant='body2'
                className={classNames({
                  [classes.remaining]: membership.total.due > 0,
                  [classes.fullyPaid]: membership.total.due <= 0,
                })}
              >
                {i18n.l('currency', membership.total.due / 100)}
              </Typography>
            </ListItem>
          </List>
        </Paper>
      </Grid>
    );
  }

  /* */
  renderMemberDetails(member, i) {
    const { classes } = this.props;
    const { membership } = this.state;

    const showSubtotal = membership.members.length > 1 && member.lines.length > 1;
    const subtotal = member.lines.reduce((counter, line) => counter + line.amount, 0);

    return (
      <Box key={`member-${i}`}>
        <div className={classes.listTitle}>
          <AccountIcon className='mr-10' />
          <Typography variant='subtitle1'>{`${member.firstName} ${member.lastName.toUpperCase()}`}</Typography>
        </div>
        <Paper className={classes.listContainer}>
          <List disablePadding>
            {member.lines.map((line, j) => (
              <ListItem key={j} divider>
                <ListItemText
                  primary={line.name}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                <Typography variant='body2' className={classes.detailAmount}>
                  {i18n.l('currency', line.amount / 100)}
                </Typography>
              </ListItem>
            ))}
            {showSubtotal && (
              <ListItem>
                <ListItemText
                  primary='Sous-total'
                  classes={{ primary: classes.bold }}
                  primaryTypographyProps={{
                    color: 'primary',
                    variant: 'body2',
                  }}
                />
                <Typography color='primary' variant='body2' className={classes.bold}>
                  {i18n.l('currency', subtotal / 100)}
                </Typography>
              </ListItem>
            )}
          </List>
        </Paper>
      </Box>
    );
  }

  /* */
  renderPaymentGrid() {
    const { creditCard, isFullyCompleted, golf } = this.state;

    if (isFullyCompleted) {
      return (
        <Grid item md={6}>
          <Box display='flex' flexDirection='column' alignItems='center'>
            <CheckCircleOutlineIcon fontSize='large' color='primary' />
            <br />
            <Typography gutterBottom color='primary'>
              {'Votre paiement a bien été enregistré.'}
            </Typography>
            <Typography gutterBottom color='primary'>
              {'Une confirmation vous a été envoyé par e-mail.'}
            </Typography>
          </Box>
        </Grid>
      );
    }

    return (
      <Grid item md={6}>
        <Typography variant='h6'>{'Paiement'}</Typography>
        <br />
        <Payment
          display='column'
          imprint={creditCard}
          golf={golf}
          currency={this.currency}
          session3DS={this.session3DS}
          defaultImprintUse='disabled'
          amount={this.transaction.amount}
          isPaylineProvider={PaymentProvider.isPayline(golf)}
          payWithNewCard={this.payWithNewCard}
          payWithNewCardPayline={this.payWithNewCardPayline}
          payWithCardImprint={this.payWithCardImprint}
        />
        <div id='PaylineWidget' />
      </Grid>
    );
  }

  /* */
  setMembershipPayment(transactionPaybox) {
    const { golf, account, membership } = this.state;

    const title = `${membership.members[0].category}`;

    return Membership.api.setMembershipPayment(apolloClient, {
      title,
      golfId: golf.id,
      accountId: account.id,
      year: membership.year,
      transaction: transactionPaybox,
      reference: membership.reference,
    });
  }

  /* */
  payWithNewCard = (values, { setSubmitting }) => {
    const { membership } = this.state;
    const { amount, reference } = this.transaction;
    const { id3D = null } = values;

    const creditCard = {
      ...values,
      name: '3DS web',
      number: values.number.replace(/\s/g, ''),
      dueDate: values.dueDate.replace(/\D/g, ''),
    };

    this.setState({
      error: null,
      buttonLoading: true,
      status: 'Nous interrogeons votre banque...',
    });

    return Payline.api
      .capture(apolloClient, {
        id3D,
        amount,
        reference,
        creditCard: helpers.object.filterByKeys(creditCard, ['number', 'dueDate', 'cvv']),
      })
      .then((res) => {
        this.setState({ status: 'Nous confirmons votre paiement au golf...' });

        return this.setMembershipPayment({
          amount,
          callNo: res.authorizationNumber || res.NUMAPPEL,
          transactionNo: res.transactionId || res.NUMTRANS,
          card: CreditCard.functions.anonymizeNumber(creditCard.number),
          ...helpers.object.filterByKeys(creditCard, ['type', 'brand', 'country']),
        });
      })
      .then(() => {
        membership.total.due -= amount;
        membership.total.paid += amount;
        this.setState({ isFullyCompleted: true });
      })
      .catch((err) =>
        this.setState({
          status: null,
          error: err.message,
        }),
      )
      .finally(() => {
        setSubmitting(false);
        this.setState({ buttonLoading: false });
      });
  };

  payWithNewCardPayline = () => {
    const { account, membership } = this.state;
    const { amount } = this.transaction;

    const data = {
      amount,
      reference: membership.reference,
    };

    this.setState({
      error: null,
      waiting: true,
      status: 'Nous préparons le paiement...',
      buttonLoading: true,
    });

    const baseUrl = window.location.origin;
    const url = new URL(window.location.href);
    url.searchParams.set('payline', 'success');
    const returnUrl = `${baseUrl}${url.pathname}${url.search}`;
    const cancelUrl = window.location.href;

    return Payline.api
      .getToken(apolloClient, {
        amount: data.amount,
        reference: data.reference,
        returnUrl,
        cancelUrl,
        callbackUrl: `${baseUrl}${url.pathname}?payline=callback`,
        customer: {
          firstName: account.firstname,
          lastName: account.lastname,
          email: account.email,
          phone: account.phone,
        },
        currency: this.currency,
      })
      .then((res) => {
        if (!res || !res.token) {
          throw new Error('Token de paiement introuvable.');
        }

        this.paylineWidgetData = {
          ...data,
          token: res.token,
        };
        this.isPaylinePaymentHandled = false;

        this.setState({
          paylineToken: res.token,
          waiting: false,
          status: null,
          buttonLoading: false,
        });

        return FullyPayMembership.loadPaylineScript().then(() => {
          this.showPaylineWidget(res.token);
        });
      })
      .catch((err) => {
        this.setState({
          error: err.message,
          waiting: false,
          status: null,
          paylineToken: null,
          buttonLoading: false,
        });
        this.removePaylineWidget();
      });
  };

  /* */
  payWithCardImprint = () => {
    const { creditCard, membership, golf } = this.state;
    const { amount, reference } = this.transaction;

    // Garde-fou (défense en profondeur) : ne jamais tenter un paiement 1-clic avec une
    // carte dépourvue de wallet Payline réel. Normalement déjà filtré au chargement.
    if (!creditCard || !creditCard.walletId) {
      this.setState({
        error:
          "Cette carte enregistrée n'est plus utilisable pour le paiement en 1 clic. Réglez avec une nouvelle carte : elle sera ré-enregistrée automatiquement.",
      });
      return;
    }

    const subscriber = {
      // Correction : débiter le wallet Payline réel (walletId), pas l'id du credit_card.
      // subscriber.id doit porter le walletId, sinon subscriber.id=null côté API et la
      // mutation est rejetée (aligné sur les autres écrans de paiement).
      id: creditCard.walletId,
      token: creditCard.token,
      dueDate: creditCard.dueDate,
    };

    this.setState({
      error: null,
      buttonLoading: true,
      status: 'Nous interrogeons votre banque...',
    });

    const captureApi = Payline.api.captureSubscriber;

    captureApi(apolloClient, {
      amount,
      reference,
      subscriber,
    })
      .then((res) => {
        // Handle 3DS challenge required for wallet payments
        if (res && res.needs3DS) {
          if (res.threeDSData?.actionUrl) {
            // ACS redirect URL present: show the 3DS challenge dialog
            this.setState({
              buttonLoading: false,
              status: null,
              wallet3DS: {
                open: true,
                threeDSData: res.threeDSData,
                subscriber,
                amount,
                reference,
              },
            });
            return;
          }
          // No ACS URL: fall back to Payline hosted widget with saved card pre-selected
          this.setState({ buttonLoading: false, status: 'Chargement du formulaire de paiement...' });
          const { account, membership } = this.state;
          const baseUrl = window.location.origin;
          const url = new URL(window.location.href);
          url.searchParams.set('payline', 'success');
          const returnUrl = `${baseUrl}${url.pathname}${url.search}`;
          const cancelUrl = window.location.href;
          return Payline.api.getToken(apolloClient, {
            amount,
            reference,
            walletId: subscriber.id,
            returnUrl,
            cancelUrl,
            callbackUrl: `${baseUrl}${url.pathname}?payline=callback`,
            customer: {
              firstName: account.firstname,
              lastName: account.lastname,
              email: account.email,
            },
            currency: this.currency,
          }).then((tokenRes) => {
            if (!tokenRes?.token) throw new Error('Token de paiement introuvable.');
            this.paylineWidgetData = { amount, reference, token: tokenRes.token };
            this.isPaylinePaymentHandled = false;
            this.setState({ paylineToken: tokenRes.token, waiting: false, status: null });
            return FullyPayMembership.loadPaylineScript().then(() => {
              this.showPaylineWidget(tokenRes.token);
            }).then(() => {
              // Stop the original promise chain: the widget callback handles payment confirmation.
              const stopErr = new Error('WIDGET_FALLBACK');
              stopErr.is3DS = true;
              throw stopErr;
            });
          });
        }

        this.setState({ status: 'Nous confirmons votre paiement au golf...' });

        return this.setMembershipPayment({
          amount,
          callNo: res.authorizationNumber || res.NUMAPPEL,
          transactionNo: res.transactionId || res.NUMTRANS,
          card: creditCard.number,
          creditCardId: creditCard.id,
          ...helpers.object.filterByKeys(creditCard, ['type', 'brand', 'country']),
        });
      })
      .then(() => {
        membership.total.due -= amount;
        membership.total.paid += amount;
        this.setState({ isFullyCompleted: true });
      })
      .catch((err) =>
        this.setState({
          status: null,
          error: err.message,
        }),
      )
      .finally(() => {
        this.setState({ buttonLoading: false });
      });
  };

  handleWallet3DSClose = () => {
    this.setState({
      wallet3DS: {
        open: false,
        threeDSData: null,
        subscriber: null,
        amount: null,
        reference: null,
      },
      error: 'Authentification 3D Secure annul\u00e9e.',
    });
  };

  // Verrou de ré-entrée SYNCHRONE : un double message ACS (POST 3DS) ne doit pas rejouer le débit.
  _wallet3DSInFlight = false;

  handleWallet3DSComplete = ({ pares, md }) => {
    if (this._wallet3DSInFlight) return;
    this._wallet3DSInFlight = true;
    const { wallet3DS } = this.state;
    const { membership } = this.state;

    this.setState({
      wallet3DS: {
        open: false,
        threeDSData: null,
        subscriber: null,
        amount: null,
        reference: null,
      },
      buttonLoading: true,
      status: 'Nous interrogeons votre banque...',
    });

    const { amount } = wallet3DS;
    const { creditCard } = this.state;

    Payline.api
      .captureSubscriberAfter3DS(apolloClient, {
        amount: wallet3DS.amount,
        reference: wallet3DS.reference,
        walletId: wallet3DS.subscriber.id,
        pares,
        md,
      })
      .then((res) => {
        this.setState({ status: 'Nous confirmons votre paiement au golf...' });

        return this.setMembershipPayment({
          amount,
          callNo: res.authorizationNumber || res.NUMAPPEL,
          transactionNo: res.transactionId || res.NUMTRANS,
          card: creditCard.number,
          creditCardId: creditCard.id,
          ...helpers.object.filterByKeys(creditCard, ['type', 'brand', 'country']),
        });
      })
      .then(() => {
        membership.total.due -= amount;
        membership.total.paid += amount;
        this.setState({ isFullyCompleted: true });
      })
      .catch((err) =>
        this.setState({
          status: null,
          error: err.message || 'Le paiement a \u00e9chou\u00e9.',
        }),
      )
      .finally(() => {
        this._wallet3DSInFlight = false;
        this.setState({ buttonLoading: false });
      });
  };

  /* */
  render() {
    const {
      // ux
      error,
      status,
      buttonLoading,
      screenLoading,
      // data
      account,
      membership,
    } = this.state;

    return (
      <Screen noDrawer error={error} loading={screenLoading}>
        {account && membership && (
          <React.Fragment>
            {this.renderHeader()}
            <Grid container spacing={40} justify='center'>
              {this.renderInfosGrid()}
              {this.renderPaymentGrid()}
            </Grid>
            <DialogPaymentIP open={buttonLoading} status={status} />
            <DialogWallet3DS
              open={this.state.wallet3DS.open}
              threeDSData={this.state.wallet3DS.threeDSData}
              onClose={this.handleWallet3DSClose}
              onComplete={this.handleWallet3DSComplete}
            />
          </React.Fragment>
        )}
      </Screen>
    );
  }
}

export default withStyles(styles)(FullyPayMembership);
